import { useEffect } from 'react';
import { useTheme, hasStoredTheme } from '../../theme/ThemeContext';
import useChurchConfig from '../../hooks/useChurchConfig';
import { uiScaleFactor } from '../../utils/churchSettings';

/**
 * Keeps the live app in step with the parish-wide appearance settings.
 *
 * Brand colour: the provider paints from a localStorage hint so the first
 * frame is already the right colour; this pushes the authoritative value in
 * once the config request lands, and again whenever Settings saves a new one.
 *
 * Default theme: applied only to a browser that has never used the navbar
 * toggle — a personal choice always wins over the parish default.
 *
 * Display scale: a CSS zoom on the document, so every module follows without
 * knowing about it. Printing opens its own window and is unaffected.
 *
 * Renders nothing.
 */
const ChurchConfigSync = () => {
  const { brandColor, setBrandColor, setDarkMode } = useTheme();
  const config = useChurchConfig();
  const serverColor = config.themeColor || '';
  const defaultTheme = config.defaultTheme || '';
  const scale = uiScaleFactor(config.uiScale);

  useEffect(() => {
    if (serverColor !== brandColor) setBrandColor(serverColor);
  }, [serverColor, brandColor, setBrandColor]);

  useEffect(() => {
    if (defaultTheme && !hasStoredTheme()) setDarkMode(defaultTheme === 'dark');
  }, [defaultTheme, setDarkMode]);

  useEffect(() => {
    const root = document.documentElement as HTMLElement & { style: CSSStyleDeclaration & { zoom?: string } };
    root.style.zoom = scale === 1 ? '' : String(scale);
    return () => { root.style.zoom = ''; };
  }, [scale]);

  return null;
};

export default ChurchConfigSync;
